import {
  FormEvent,
  FormEventHandler,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { Modal } from '@hhgtech/hhg-components'
import { Option } from '@hhgtech/hhg-components/build/components/molecules/dropdown'
import Cookies from 'js-cookie'
import { useIntl } from 'react-intl'

import { PATHS } from 'api/paths'
import { callApiWithLead } from 'api/utils'
import { getLeadPath } from 'api/utils/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { domainLocales } from 'domainLocales'
import { useRouterExtend } from 'hooks/useRouterExtend'
import {
  assetImagePath,
  LOCALE_SPECS,
  WORKSHOP_USER_EMAIL,
  WORKSHOP_USER_PHONE,
} from 'interfaces/constants'
import { AppContext } from 'state/appContext'

import {
  StyledInput,
  StyledLabel,
  StyledPhoneInput,
  StyledScrollList,
  StyledSubmitButton,
  StyledWorkshopBody,
  StyledWorkshopForm,
  StyledWorkshopHeading,
  StyledWorkshopTitle,
} from './index.styled'
import { WorkshopSelectCard } from './workshopSelectCard'

type WorkshopFormProps = {
  isOpen: boolean
  onClose: () => void
  workshops: Workshop[]
  chosenWorkshop: Workshop | null
  isMobile: boolean
  onSuccess?: () => void
}
export const WorkshopForm = ({
  isOpen,
  onClose,
  workshops,
  isMobile,
  chosenWorkshop,
  onSuccess,
}: WorkshopFormProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [selectedWorkshops, setSelectedWorkshops] = useState<Workshop[]>([])
  const { state } = useContext(AppContext)
  const { locale } = useRouterExtend()
  const { userInfo, isLoggedIn } = state
  const inputNameRef = useRef<HTMLInputElement | null>(null)
  const inputPhoneRef = useRef<HTMLInputElement | null>(null)
  const inputEmailRef = useRef<HTMLInputElement | null>(null)
  const [areaCode, setAreaCode] = useState<string>(
    userInfo?.area_code || LOCALE_SPECS[locale].PHONE,
  )
  const [workshopIndex, setWorkshopIndex] = useState<number>(0)
  const { formatMessage: f } = useIntl()

  const closeModal = () => {
    setSelectedWorkshops([])
    onClose()
  }

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (
    e: FormEvent<HTMLFormElement>,
  ) => {
    setIsLoading(true)
    e.preventDefault()

    const name = inputNameRef.current?.value
    const email = inputEmailRef.current?.value || ''
    const phone = `${areaCode}${inputPhoneRef.current?.value || ''}`
    if (selectedWorkshops.length > 0) {
      const workshopSubscribeReq = await Promise.all(
        selectedWorkshops.map((workshop) =>
          callApiWithLead(
            getLeadPath(PATHS.LEADGEN.POST_LEAD_LEAD, {
              campaignCode: workshop.campaignId,
            }),
            'POST',
            {
              data: {
                url: window?.location?.href,
                title_article: document?.title,
                referrer: window?.location?.href,
                meta: [
                  {
                    key: 'workshop title',
                    value: workshop.name,
                    control: 'text',
                  },
                  {
                    key: 'full-name',
                    value: name,
                    control: 'text',
                  },
                  {
                    key: 'email',
                    value: email,
                    control: 'email',
                  },
                  {
                    key: 'phone',
                    value: phone,
                    control: 'tel',
                  },
                ],
              },
            },
          ).then((response) => response?._status === 1),
        ),
      )
      if (!workshopSubscribeReq.includes(false)) {
        if (isLoggedIn) {
          setSelectedWorkshops([])
          onSuccess?.()
        } else {
          Cookies.set(WORKSHOP_USER_EMAIL, email, {
            domain: `.${domainLocales[locale]}`,
          })
          Cookies.set(WORKSHOP_USER_PHONE, phone, {
            domain: `.${domainLocales[locale]}`,
          })
          redirectToSSOWithReturnUrl(
            '',
            'workshop-register',
            'workshop-success',
          )
        }
        setIsLoading(false)
      } else {
        setIsLoading(false)
      }
    }
  }

  const handleOnChangePhoneCode = (data: Option) => {
    setAreaCode(data.value as string)
  }

  useEffect(() => {
    setWorkshopIndex(
      workshops.findIndex((iWorkshop) => iWorkshop === chosenWorkshop),
    )
  }, [chosenWorkshop, workshops])

  useEffect(() => {
    if (
      chosenWorkshop &&
      !selectedWorkshops.find((iws) => iws === chosenWorkshop)
    ) {
      setSelectedWorkshops((v) => [...v, chosenWorkshop])
    }
  }, [chosenWorkshop])

  const onSelectedWorkshop = (workshop: Workshop) => {
    if (!selectedWorkshops.find((iws) => iws === workshop)) {
      setSelectedWorkshops((v) => [...v, workshop])
    } else {
      setSelectedWorkshops((_workshops) =>
        _workshops.filter((ws) => ws !== workshop),
      )
    }
  }

  return (
    <Modal
      closeIcon={
        <img
          loading="lazy"
          src={`${assetImagePath}/close-icon.svg`}
          alt="close-btn"
        />
      }
      type={isMobile ? 'confirm' : 'form'}
      isOpen={isOpen}
      onClose={closeModal}
    >
      <StyledWorkshopForm>
        <StyledWorkshopHeading>
          <StyledWorkshopTitle>
            {f({
              id: 'spotlight.landing.workshopForm.title',
              defaultMessage: 'Register to join the workshop',
            })}
          </StyledWorkshopTitle>
        </StyledWorkshopHeading>
        <StyledWorkshopBody>
          <form onSubmit={handleSubmit}>
            <StyledLabel htmlFor="name">
              {f({
                id: 'spotlight.landing.workshopForm.name',
                defaultMessage: 'Name',
              })}
            </StyledLabel>
            <StyledInput
              ref={inputNameRef}
              name="name"
              placeholder={f({
                id: 'spotlight.landing.workshopForm.name.placeholder',
                defaultMessage: 'Type your name',
              })}
              defaultValue={userInfo?.name}
              required
            />

            <StyledLabel>
              {f({
                id: 'spotlight.landing.workshopForm.phone',
                defaultMessage: 'Phone',
              })}
            </StyledLabel>
            <StyledPhoneInput
              ref={inputPhoneRef}
              defaultValue={{
                phone: userInfo?.phone,
                countryPhoneCode: userInfo?.area_code,
              }}
              onChangeCode={handleOnChangePhoneCode}
              required={true}
            />

            <StyledLabel htmlFor="email">
              {f({
                id: 'spotlight.landing.workshopForm.email',
                defaultMessage: 'Email',
              })}
            </StyledLabel>
            <StyledInput
              name="email"
              ref={inputEmailRef}
              placeholder={f({
                id: 'spotlight.landing.workshopForm.email.placeholder',
                defaultMessage: 'Type your email',
              })}
              defaultValue={userInfo?.email}
              type="email"
              required
            />

            <StyledLabel>
              {f({
                id: 'spotlight.landing.workshopForm.workshop',
                defaultMessage: 'Choose workshop',
              })}
            </StyledLabel>
            <StyledScrollList
              arrowType="Care"
              page={workshopIndex}
              onChangePage={setWorkshopIndex}
              row={1}
              column={1}
              isDotsShown={isMobile && workshops.length > 1}
              isArrowShown={!isMobile}
              distanceOverlappedLeft={{
                amount: 16,
                unit: 'pixel',
              }}
              mode="snapped"
            >
              {workshops.map((workshop, idx) => (
                <WorkshopSelectCard
                  isSelected={!!selectedWorkshops.find((iW) => iW === workshop)}
                  key={idx}
                  workshop={workshop}
                  onSelected={onSelectedWorkshop}
                />
              ))}
            </StyledScrollList>
            <StyledSubmitButton
              type="submit"
              disabled={selectedWorkshops.length === 0 || isLoading}
            >
              {f({
                id: 'spotlight.landing.workshopForm.register',
                defaultMessage: 'Register',
              })}
            </StyledSubmitButton>
          </form>
        </StyledWorkshopBody>
      </StyledWorkshopForm>
    </Modal>
  )
}
